/**
 * Pole Store - Zustand state management for poles
 */
import { create } from 'zustand';
import { Pole } from '../types';
import { api } from '../services/api';
import { getErrorMessage } from '../utils/errorMessages';

interface PoleState {
  // State
  poles: Pole[];
  selectedPole: Pole | null;
  loading: boolean;
  error: string | null;
  zoomTarget: { lat: number; lng: number; zoom: number } | null; // Target for map zoom
  // Stĺpik, ktorého kazetu má mapa otvoriť hneď po načítaní. Používa to hľadanie
  // zákazníka: kým sa prepne projekt a dotiahnu stĺpy, pole ešte neexistuje,
  // takže sa odloží jeho id a mapa dialóg otvorí, až keď ho má.
  otvorKazetuPreStlp: number | null;

  // Actions
  fetchPoles: (projectId: number) => Promise<void>;
  createPole: (projectId: number, data: any) => Promise<Pole>;
  updatePole: (projectId: number, poleId: number, data: Partial<Pole>) => Promise<void>;
  updatePoleLocal: (updatedPole: Pole) => void; // Update pole in store without API call
  deletePole: (projectId: number, poleId: number) => Promise<void>;
  setSelectedPole: (pole: Pole | null) => void;
  setZoomTarget: (target: { lat: number; lng: number; zoom: number } | null) => void;
  setOtvorKazetuPreStlp: (poleId: number | null) => void;
  clearPoles: () => void;
  clearError: () => void;
}

export const usePoleStore = create<PoleState>((set, get) => ({
  // Initial state
  poles: [],
  selectedPole: null,
  loading: false,
  error: null,
  zoomTarget: null,
  otvorKazetuPreStlp: null,

  // Fetch all poles
  fetchPoles: async (projectId: number) => {
    set({ loading: true, error: null });
    try {
      const response = await api.poles.getAll(projectId);
      set({ poles: response.data, loading: false });
    } catch (error: any) {
      set({ error: getErrorMessage(error, 'Nepodarilo sa načítať stĺpiky.'), loading: false });
    }
  },

  // Create pole
  createPole: async (projectId: number, data: any) => {
    set({ loading: true, error: null });
    try {
      const response = await api.poles.create(projectId, data);
      const pole = response.data;
      set((state) => ({
        poles: [...state.poles, pole],
        loading: false,
      }));
      return pole;
    } catch (error: any) {
      set({ error: getErrorMessage(error, 'Nepodarilo sa vytvoriť stĺpik.'), loading: false });
      throw error;
    }
  },

  // Update pole
  updatePole: async (projectId: number, poleId: number, data: Partial<Pole>) => {
    set({ loading: true, error: null });
    try {
      console.log('[PoleStore] updatePole called:', { projectId, poleId, data });
      console.log('[PoleStore] updatePole data.custom_tasks:', data.custom_tasks);
      console.log('[PoleStore] updatePole data.installation_checklist:', data.installation_checklist);
      const response = await api.poles.update(projectId, poleId, data);
      console.log('[PoleStore] updatePole response:', response);
      console.log('[PoleStore] updatePole response.data:', response.data);
      console.log('[PoleStore] updatePole response.data.pole:', response.data.pole);

      // Response can be either:
      // 1. New format: { pole: {...}, updated_pole_ids: [...] }
      // 2. Old format: { id: ..., name: ..., ... } (direct pole object)
      const updated = response.data.pole || response.data;
      const updatedPoleIds = response.data.updated_pole_ids || [];

      console.log('[PoleStore] Updated pole:', updated);
      console.log('[PoleStore] Updated pole installation_checklist:', updated.installation_checklist);
      console.log('[PoleStore] Updated pole custom_tasks:', updated.custom_tasks);
      console.log('[PoleStore] Updated pole IDs from color propagation:', updatedPoleIds);

      // Update the main pole
      set((state) => ({
        poles: state.poles.map((p) => (p.id === poleId ? updated : p)),
        selectedPole: state.selectedPole?.id === poleId ? updated : state.selectedPole,
        loading: false,
      }));

      // If there are updated poles from color propagation, fetch them
      if (updatedPoleIds.length > 0) {
        console.log('[PoleStore] Fetching updated poles:', updatedPoleIds);
        // Fetch all updated poles
        const updatedPoles = await Promise.all(
          updatedPoleIds.map(async (id: number) => {
            try {
              const poleResponse = await api.poles.getById(projectId, id);
              return poleResponse.data;
            } catch (err) {
              console.error(`[PoleStore] Failed to fetch pole ${id}:`, err);
              return null;
            }
          })
        );

        console.log('[PoleStore] Fetched updated poles, updating store');
        // Update the poles in the store
        set((state) => ({
          poles: state.poles.map((p) => {
            const updatedPole = updatedPoles.find((up) => up && up.id === p.id);
            return updatedPole || p;
          }),
        }));
        console.log('[PoleStore] Store updated with fresh pole data');
      }

      // IMPORTANT: Set loading to false AFTER all updates are done
      set({ loading: false });
    } catch (error: any) {
      console.error('[PoleStore] updatePole error:', error);
      set({ error: getErrorMessage(error, 'Nepodarilo sa uložiť zmeny stĺpika.'), loading: false });
      throw error;
    }
  },

  // Update pole locally (without API call) - useful for photo updates
  updatePoleLocal: (updatedPole: Pole) => {
    console.log('[PoleStore] updatePoleLocal called:', { poleId: updatedPole.id, photosCount: updatedPole.photos?.length });
    set((state) => ({
      poles: state.poles.map((p) => (p.id === updatedPole.id ? updatedPole : p)),
      selectedPole: state.selectedPole?.id === updatedPole.id ? updatedPole : state.selectedPole,
    }));
  },

  // Delete pole
  deletePole: async (projectId: number, poleId: number) => {
    set({ loading: true, error: null });
    try {
      await api.poles.delete(projectId, poleId);
      set((state) => ({
        poles: state.poles.filter((p) => p.id !== poleId),
        selectedPole: state.selectedPole?.id === poleId ? null : state.selectedPole,
        loading: false,
      }));
    } catch (error: any) {
      set({ error: getErrorMessage(error, 'Nepodarilo sa zmazať stĺpik.'), loading: false });
      throw error;
    }
  },

  // Set selected pole
  setSelectedPole: (pole) => {
    set({ selectedPole: pole });
  },

  // Set zoom target
  setZoomTarget: (target) => {
    set({ zoomTarget: target });
  },

  // Odloženie požiadavky „otvor kazetu tohto stĺpika", kým sa stĺpy načítajú
  setOtvorKazetuPreStlp: (poleId) => {
    set({ otvorKazetuPreStlp: poleId });
  },

  // Clear poles
  clearPoles: () => {
    set({ poles: [], selectedPole: null, otvorKazetuPreStlp: null });
  },

  // Clear error
  clearError: () => {
    set({ error: null });
  },
}));

